import React, { useState, useEffect, useRef } from 'react';
import { useApiStore } from '@core/store';
import BaseSelector from '@/components/selectors/BaseSelector';
import { useTranslation } from 'react-i18next';

export interface AllShopsSelectorProps {
    allShopsSelectedOnStart?: boolean;
    initialSelectedShopIds?: string[];
    onSelectedShopsChanged?: (selectedShops: string[]) => void;
    value?: string[];
    onChange?: (selectedShops: string[]) => void;
    sx?: any;
    label?: string;
}

const AllShopsSelector: React.FC<AllShopsSelectorProps> = ({
    allShopsSelectedOnStart = false,
    initialSelectedShopIds,
    onSelectedShopsChanged,
    value,
    onChange,
    sx,
    label
}) => {
    const { t } = useTranslation();

    const isInitializedRef = useRef(false);
    const isControlled = value !== undefined && onChange !== undefined;

    const [selectedShops, setSelectedShops] = useState<string[]>(initialSelectedShopIds ?? []);
    const { shopIds, loadShopIds, isLoading } = useApiStore();

    const activeSelected = isControlled ? value : selectedShops;

    const handleChange = (val: string[]) => {
        if (isControlled) {
            onChange!(val);
        } else {
            setSelectedShops(val);
        }
    };

    useEffect(() => {
        if (!isControlled) {
            onSelectedShopsChanged?.(selectedShops);
        }
    }, [selectedShops, onSelectedShopsChanged, isControlled]);

    useEffect(() => {
        if (isInitializedRef.current) return;

        const initializeShops = async () => {
            try {
                if (shopIds.length === 0 && !isLoading) {
                    await loadShopIds();
                }

                if (!isControlled && allShopsSelectedOnStart && !initialSelectedShopIds?.length && shopIds.length > 0) {
                    setSelectedShops(shopIds.map((x) => x.id));
                }
                isInitializedRef.current = true;
            } catch (error) {
                console.error('[AllShopsSelector] Error initializing shops:', error);
            }
        };

        initializeShops();
    }, [shopIds.length, isLoading, loadShopIds, allShopsSelectedOnStart, initialSelectedShopIds, isControlled]);

    const items = shopIds.map((shop) => ({ value: shop.id, text: shop.name }));

    return (
        <BaseSelector
            multiple
            selectAll
            searchable
            value={activeSelected}
            items={items}
            itemText="text"
            itemValue="value"
            label={label ?? (isControlled ? undefined : 'Shops')}
            disabled={isLoading}
            sx={sx}
            onChange={handleChange}
            selectDisplayLabels={{
                allLabel: t('allShops'),
                placeholderLabel: t('selectShop'),
                countLabel: t('selectedWithValue')
            }}
        />
    );
};

export default AllShopsSelector;
